const RATIO_FIELDS = new Set([
  "min_weighted_coverage_pct",
  "max_outlier_deviation_pct",
  "max_market_adjustment_pct",
]);

export const DEFAULT_MARKET_BENCHMARK_POLICY = Object.freeze({
  is_enabled: false,
  max_data_age_minutes: 1440,
  min_competitors: 2,
  min_weighted_coverage_pct: 0.5,
  max_outlier_deviation_pct: 0.35,
  max_market_adjustment_pct: 0.12,
  fallback_action: "ignore_market",
});

function finiteNumber(value, fallback) {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : fallback;
}

export function normalizeMarketBenchmarkPolicyDraft(runtime = {}) {
  const source = runtime?.item || runtime?.snapshot || {};
  return {
    expected_configuration_hash: runtime?.configuration_hash || null,
    item: {
      is_enabled:
        typeof source.is_enabled === "boolean"
          ? source.is_enabled
          : DEFAULT_MARKET_BENCHMARK_POLICY.is_enabled,
      max_data_age_minutes: finiteNumber(
        source.max_data_age_minutes,
        DEFAULT_MARKET_BENCHMARK_POLICY.max_data_age_minutes
      ),
      min_competitors: finiteNumber(
        source.min_competitors,
        DEFAULT_MARKET_BENCHMARK_POLICY.min_competitors
      ),
      min_weighted_coverage_pct: finiteNumber(
        source.min_weighted_coverage_pct,
        DEFAULT_MARKET_BENCHMARK_POLICY.min_weighted_coverage_pct
      ),
      max_outlier_deviation_pct: finiteNumber(
        source.max_outlier_deviation_pct,
        DEFAULT_MARKET_BENCHMARK_POLICY.max_outlier_deviation_pct
      ),
      max_market_adjustment_pct: finiteNumber(
        source.max_market_adjustment_pct,
        DEFAULT_MARKET_BENCHMARK_POLICY.max_market_adjustment_pct
      ),
      fallback_action: "ignore_market",
    },
  };
}

export function updateMarketBenchmarkPolicyDraft(draft, field, value) {
  if (!(field in DEFAULT_MARKET_BENCHMARK_POLICY)) return draft;
  const normalized = RATIO_FIELDS.has(field)
    ? Number(value)
    : field === "is_enabled"
      ? value === true
      : field === "fallback_action"
        ? "ignore_market"
        : Number(value);
  return {
    ...draft,
    item: {
      ...(draft?.item || DEFAULT_MARKET_BENCHMARK_POLICY),
      [field]: normalized,
    },
  };
}

export function validateMarketBenchmarkPolicyDraft(draft) {
  const item = draft?.item || {};
  const issues = [];
  if (!/^[0-9a-f]{64}$/.test(String(draft?.expected_configuration_hash || ""))) {
    issues.push("Reload the market quality policy before saving.");
  }
  if (typeof item.is_enabled !== "boolean") {
    issues.push("Market quality enabled must be true or false.");
  }
  if (!Number.isInteger(item.max_data_age_minutes) || item.max_data_age_minutes < 5 || item.max_data_age_minutes > 10080) {
    issues.push("Maximum market data age must be between 5 and 10080 minutes.");
  }
  if (!Number.isInteger(item.min_competitors) || item.min_competitors < 1 || item.min_competitors > 20) {
    issues.push("Minimum competitors must be between 1 and 20.");
  }
  [
    ["min_weighted_coverage_pct", 0, 1, "Weighted coverage"],
    ["max_outlier_deviation_pct", 0.01, 2, "Outlier deviation"],
    ["max_market_adjustment_pct", 0, 0.2, "Market influence"],
  ].forEach(([field, min, max, label]) => {
    if (!Number.isFinite(item[field]) || item[field] < min || item[field] > max) {
      issues.push(`${label} must stay between ${min * 100}% and ${max * 100}%.`);
    }
  });
  if (item.fallback_action !== "ignore_market") {
    issues.push("Low-quality market data must always be ignored.");
  }
  return issues;
}

export function buildMarketBenchmarkPolicyPayload(draft) {
  return {
    expected_configuration_hash: draft.expected_configuration_hash,
    item: {
      is_enabled: draft.item.is_enabled === true,
      max_data_age_minutes: Number(draft.item.max_data_age_minutes),
      min_competitors: Number(draft.item.min_competitors),
      min_weighted_coverage_pct: Number(draft.item.min_weighted_coverage_pct),
      max_outlier_deviation_pct: Number(draft.item.max_outlier_deviation_pct),
      max_market_adjustment_pct: Number(draft.item.max_market_adjustment_pct),
      fallback_action: "ignore_market",
    },
  };
}
